<template>
  <div :class="['fip-preview', { 'active': isImageActive, 'show': showPreview }]">
    <div class="fip-preview-left">
      <div
        :class="{'fip-background-wrapper-cover': isImageCover, 'fip-background-wrapper': !isImageCover}">
        <picture
          v-if="!imageFailedToLoad"
          :class="{ 'landscape': isLandscapeImage() }">
          <source
            v-for="(picture, index) in imageData.pictures"
            :key="index"
            :srcset="picture.source"
            :media="picture.media"
            :type="picture.type">
          <img
            ref="fipImg"
            v-on-complete="onImageLoad"
            :src="imageData.image.url"
            :alt="item.title"
            height="320px"
            width="570px"
            class="fip-background"
            @error="onImageError"
            @load="onImageLoad">
        </picture>
        <img
          v-else
          v-on-complete="onImageLoad"
          :alt="item.title"
          width="570px"
          height="320px"
          src="/images/cover-default.jpg"
          class="fip-background"
          @load="onImageLoad">
      </div>

      <div
        v-if="showProgressBar"
        class="wptv-progress-bar text-above show-from-large">
        <wptv-progress-bar
          :progress-value="progressValue"
          :disabled="locationExpired"
          :text="$t('panelItem.timeLeft', { time: remainingTime })"
          :text-above="true"
          class="mt-0"
          data-wptv="progress-bar-mosaic-item" />
      </div>
      <button
        v-if="hasTrailer && trailerIsCompatible"
        v-tealium:click="$stats.fip.onClickPreview(item)"
        data-wptv="banniere-play_BA-icone"
        type="button"
        class="btn-trailer"
        @mouseover="isImageActive = true"
        @mouseleave="isImageActive = false"
        @click="play()">
        <span class="icon icon-Play" aria-hidden="true" />
        {{ $t('fip.trailer') }}
      </button>
    </div>
    <div
      class="fip-preview-right">
      <fip-title
        :item="item"
        :can-be-bookmarked="showBookmarkIcon"
        :pre-title="preTitle"
        class="fip-preview-title" />
      <div
        v-if="showProgressBar"
        class="wptv-progress-bar hide-from-large">
        <wptv-progress-bar
          :progress-value="progressValue"
          :disabled="locationExpired"
          :text="$t('panelItem.timeLeft', { time: remainingTime })"
          data-wptv="progress-bar-mosaic-item" />
      </div>
      <div class="show-from-large">
        <div class="fip-preview-mentions-wrapper">
          <fip-mentions
            :item="item"
            class="fip-preview-mentions" />
          <fip-user-informations
            :item="item"
            class="fip-preview-user-infos" />
        </div>
        <rating
          v-if="showRating"
          :item="item"
          class="fip-preview-rating" />
        <participants-list
          v-if="!!artists"
          :artists="artists"
          class="infos-artists"
          data-wptv="fip-artists" />
        <span
          v-if="item.synopsis"
          class="fip-preview-synopsis"
          data-wptv="fip-synopsis">
          {{ item.synopsis }}
        </span>

        <fip-informations :item="item" />
      </div>
    </div>
  </div>
</template>

<script>
import _get from 'lodash/get'
import { mapGetters } from 'vuex'
import { setItemImageData } from '../../../../shared/imageData.utils'
import { WptvProgressBar } from '@wptv/components/ui/progressBar'
import FipInformations from '../../fipInformations/FipInformations'
import FipMentions from '../../fipMentions/FipMentions'
import FipTitle from '../fipTitle/FipTitle'
import FipUserInformations from '../../fipUserInformations/FipUserInformations'
import FipUtils from '../../fip.utils'
import ParticipantsList from '../../participantsList/ParticipantsList.vue'
import Rating from '../../rating/Rating'

export default {
  title: 'FipPreview',
  directives: {
    // Surcharge pour safari qui n'appelle pas le onLoad pour les images en cache
    // du coup les vignettes ne n'affichent pas @see WEBTV-4914
    onComplete (el, { value }) {
      if (el.complete) {
        value()
      }
    }
  },
  components: {
    FipUserInformations,
    FipInformations,
    FipMentions,
    WptvProgressBar,
    ParticipantsList,
    Rating,
    FipTitle
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    canBeBookmarked: {
      type: Boolean,
      default: false
    },
    preTitle: {
      type: String,
      default: ''
    }
  },
  data () {
    const imageData = setItemImageData(this.item, {}, this.$config)

    return {
      isImageActive: false,
      showPreview: false,
      imageFailedToLoad: false,
      isImageCover: _get(imageData, 'image.format') === 'COVER',
      imageData
    }
  },
  computed: {
    ...mapGetters({
      findPlayHistoryById: 'user/findPlayHistoryById',
      terminalModel: 'terminals/getTerminalModel',
      isMac: 'useragent/isMac'
    }),
    trailerIsCompatible () {
      return _get(this.item, 'trailers[0].terminalModels', []).includes(this.terminalModel)
    },
    hasTrailer () {
      return !!_get(this.item, 'trailers[0]', null)
    },
    showCsaPanel () {
      return this.item.csa && this.item.csa > 1
    },
    locationExpired () {
      return FipUtils.isLocationExpired(this.$store, this.item.id)
    },
    showBookmarkIcon () {
      return this.canBeBookmarked && this.aTesting && !this.isProspect && !this.isPlaying
    },
    playHistory () {
      return this.findPlayHistoryById(this.item.id)
    },
    showProgressBar () {
      return this.playHistory && this.item.type === 'VIDEO'
    },
    timecode () {
      return _get(this.playHistory, 'resumeTimecode', 0)
    },
    duration () {
      return _get(this.item, 'duration', 1) * 60000
    },
    progressValue () {
      return Math.max(this.timecode * 100 / this.duration, 2)
    },
    remainingTime () {
      return Math.round((this.duration - this.timecode) / 60000)
    },
    artists () {
      const artists = {}

      if (this.item.directors && this.item.directors.length > 0) {
        artists.DIR = this.item.directors
      }
      if (this.item.actors && this.item.actors.length > 0) {
        artists.ACT = this.item.actors
      }

      if (this.item.stageDirectors && this.item.stageDirectors.length > 0) {
        artists.MES = this.item.stageDirectors
      }

      if (this.item.authors && this.item.authors.length > 0) {
        artists.AUT = this.item.authors
      }

      if (this.item.presenters && this.item.presenters.length > 0) {
        artists.PRE = this.item.presenters
      }
      if (this.item.producers && this.item.producers.length > 0) {
        artists.PRO = this.item.producers
      }

      if (this.item.choregraphs && this.item.choregraphs.length > 0) {
        artists.CHO = this.item.choregraphs
      }
      return artists
    },
    isProspect () {
      return FipUtils.isProgramProspected(this.item)
    },
    showRating () {
      return !!(_get(this.item, 'allocineRatings.user') || _get(this.item, 'allocineRatings.press'))
    }
  },
  mounted () {
    if (this.$refs.fipImg && this.$refs.fipImg.complete) {
      this.onImageLoad()
    }
  },
  methods: {
    play () {
      this.$root.$emit('watchTrailer', {
        trailer: this.item
      })
    },
    isLandscapeImage () {
      return this.$refs?.fipImg?.getBoundingClientRect ? this.$refs.fipImg.getBoundingClientRect().width > this.$refs.fipImg.getBoundingClientRect().height : false
    },
    /**
     * Toggles the showPreview to show cover
     */
    onImageLoad () {
      this.showPreview = true
    },
    /**
     * Set imageFailedToLoad to true
     */
    onImageError () {
      this.imageFailedToLoad = true
      this.isImageCover = false
    }
  }
}
</script>

<style lang="scss" scoped>
  .fip-preview {
    color: $white;
    max-width: 1280px;
    margin: auto;
    opacity: 0;
    display: flex;
    position: relative;
    background-size: cover;
    min-height: 210px;
    @include mediaQueries('flex-direction', (xsmall: column, large: row));
    @include mediaQueries('padding', (large: 0 55px));
    // Animation; used for better transition when switching between cover and banner
    &.show {
      opacity: 1;
      transition: opacity 0.3s ease-out;
      display: flex;
    }

    .fip-preview-left {
      position: relative;
      // overflow: hidden;
      min-height: 180px;
      @include mediaQueries('height', (large: 250px, xlarge: 320px));
      background: none;

      // Style for cover and banner/default
      .fip-background-wrapper-cover,
      .fip-background-wrapper {
        position: relative;
        z-index: 1;
        width: 100%;
        background: none;
        @include mediaQueries(height, (large: calc(100% - 4px)));

        .fip-background {
          @include mediaQueries('height', (large: 100%));
        }
      }

      .fip-background-wrapper {
        overflow: hidden;

        // gradient
        &:after {
          pointer-events: none;
          content: '';
          position: absolute;
          z-index: 2;
          bottom: 0;
          left: 0;
          background-image: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.4),
            rgba(0, 0, 0, 0) 54%,
            rgba(0, 0, 0, 0) 60%,
            #000000);
          width: 100%;
          height: 100%;
          @include mediaQueries('height', (xsmall: 100%, large: 100px));
          @include mediaQueries(
            'background-image', (
              'xsmall': linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0) 54%, rgba(0, 0, 0, 0) 60%,#000000),
              'large': linear-gradient(to bottom, rgba(0, 0, 0, 0), #000000)
            )
          );
          @include mediaQueries('opacity', (large: 0.8));
        }

        picture {
          position: relative;
        }

        .fip-background{
          @include mediaQueries('width', (xsmall: 100%, large: auto));
        }
      }

      .fip-background-wrapper-cover {
        @include mediaQueries('height', (xsmall: 320px, small: 400px, medium: 250px, large: calc(100% - 4px)));
        @include mediaQueries('display', (medium: flex, large: block));
        @include mediaQueries('overflow', (xsmall: hidden, medium: visible));

        picture {
          width: 100%;
          margin: auto;
          text-align: center;
          @include mediaQueries('position', (medium: relative));

          // gradient
          &.landscape:after {
            pointer-events: none;
            content: '';
            position: absolute;
            z-index: 2;
            bottom: 0;
            left: 0;
            background-image: linear-gradient(
              to bottom,
              rgba(0, 0, 0, 0.4),
              rgba(0, 0, 0, 0) 54%,
              rgba(0, 0, 0, 0) 60%,
              #000000);
            width: 100%;
            height: 100%;
            @include mediaQueries('height', (xsmall: 100%, large: 100px));
            @include mediaQueries(
              'background-image', (
                'xsmall': linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0) 54%, rgba(0, 0, 0, 0) 60%,#000000),
                'large': linear-gradient(to bottom, rgba(0, 0, 0, 0), #000000)
              )
            );
            @include mediaQueries('opacity', (large: 0.8));
          }

          .fip-background {
            @include mediaQueries('position',(xsmall: absolute, medium: relative));
            @include mediaQueries('width', (xsmall: 100%, medium: auto));
            @include mediaQueries('top', (xsmall: 50%, medium: 0));
            @include mediaQueries('transform', (xsmall: translateY(-50%), medium: none));
          }
        }
      }

     .btn-trailer {
       color: $white;
       font-weight: bold;
       background: transparent;
       height: 20px;
       position: absolute;
       border: none;
       bottom: 25px;
       font-size: 14px;
       z-index: 9;
       text-align: left;
       padding-left: 15px;
       @include mediaQueries('padding-left', (medium: 27px, large: 15px));
       @extend %focus-visible;
      }

      // Style for banner/default
      .fip-background-wrapper {
        top: 0;
        right: 0;
      }
    }
    .text-above + .btn-trailer {
      bottom: 25px;
      @include mediaQueries('bottom', (large: 50px))
    }

    .fip-preview-right {
      z-index: 1;
      max-width: 700px;
      @include mediaQueries('flex', (large: 1));
      @include mediaQueries("padding-left", (xsmall: 15px, medium: 27px, large: 30px));
      @include mediaQueries(
        "padding-right",
        (
          xsmall: 15px,
          medium: 50px
        )
      );

      .separator {
        > span:not(:last-child) {
          &:after {
            content: ',';
            margin-right: 5px;
            display: inline-block;
          }
        }
      }

      h2 {
        @include mediaQueries('margin-top', (large: 10px, xlarge: 15px));
        @include mediaQueries('font-size', (large: 12px, xlarge: 14px));
      }

      .fip-preview-rating {
        span:first-child {
          margin-right: 5px;
        }
      }

      .fip-preview-rating,
      .infos-artists,
      .fip-preview-synopsis{
        font-weight: normal;
        @include mediaQueries('margin', (medium: 15px 0, xlarge: 20px 0));
        @include mediaQueries('font-size', (large: 16px));
        @include mediaQueries('line-height', (large: 20px, xlarge: 22px));
      }

      .fip-preview-mentions-wrapper {
        display: flex;
        align-items: flex-end;

        .fip-preview-mentions {
          display: inline-flex;
          margin-right: 5px;
        }
      }
    }

    .wptv-progress-bar {
      @include mediaQueries('width', (xsmall: 173px, large: 100%));
      position: relative;
      top: 0;
      z-index: 2;
      &.text-above {
        position: absolute;
        top: auto;
        bottom: 0;
        .progressbar {
          margin: 0;
          ::v-deep .progressbar-label {
            padding: 8px 15px;
            font-size: 0.9rem;
          }
        }
      }
    }
  }
</style>
